import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Building2, Calendar, Trophy, Target, ArrowRight, Lock } from 'lucide-react';
import { blackout } from '../services/api';
import BlackoutCountdown from '../components/BlackoutCountdown';
import BlackoutIntroVideo from '../components/BlackoutIntroVideo';

/** Public promotional + leaderboard page for the Blackout Game. Non-members
 *  land here from the top-nav link. REGISTER TO PLAY drives them to /signup. */
export default function BlackoutInfo() {
  const [board, setBoard] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [tickTotal, setTickTotal] = useState(0);

  useEffect(() => {
    blackout.leaderboard(25).then(setBoard).catch(() => setBoard([]));
    blackout.communityEarnings().then(setEarnings).catch(() => setEarnings({ totalLockedUsd: 0, earningMembers: 0 }));
  }, []);

  // Count-up ticker — animates the pot number from 0 → final total on first
  // load (and whenever `earnings` refreshes with a new total). Easing is a
  // cubic ease-out so the number sprints up then settles.
  useEffect(() => {
    const target = Number(earnings?.totalLockedUsd || 0);
    if (!earnings || target <= 0) { setTickTotal(target); return; }
    const DURATION = 1600;
    const start = performance.now();
    let frame = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      setTickTotal(target * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [earnings]);

  return (
    <div className="bg-neutral-950 text-white" data-testid="blackout-info-page">
      <div className="max-w-5xl mx-auto px-6 pt-8"><BlackoutIntroVideo /></div>
      {/* Hero */}
      <section className="relative overflow-hidden min-h-[80vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/40 via-black to-fuchsia-900/30"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(16,185,129,0.3),transparent_50%)]"/>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(217,70,239,0.25),transparent_50%)]"/>
        <div className="relative max-w-5xl mx-auto px-6 py-20">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-emerald-500/40 text-emerald-300 text-xs font-black uppercase tracking-widest backdrop-blur">
            <Zap size={14}/> The Blackout Game Is On
          </div>
          <h1 className="mt-6 text-5xl md:text-7xl font-black leading-tight tracking-tight">
            <span className="block">Black out the map.</span>
            <span className="block text-emerald-400">Fill the calendar.</span>
            <span className="block">Grow your wallet.</span>
            <span className="block text-fuchsia-400">Build the community.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-neutral-300 max-w-2xl">
            Know a Black-owned business or event we're missing? Add it. Earn points instantly, build your locked cash wallet, and shape the biggest Black business map in the country.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="blackout-info-register-cta"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-base uppercase tracking-wider shadow-xl shadow-emerald-500/30 transition">
              Register to Play <ArrowRight size={18}/>
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-white/20 hover:border-white/40 text-white font-semibold text-base">
              Already a member — Log in
            </Link>
          </div>
        </div>
      </section>

      {/* How you earn */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl md:text-4xl font-black text-center">How you earn</h2>
        <p className="text-center text-neutral-400 mt-2">Three ways. Instant points on every submission.</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          <RewardCard icon={Target} tint="amber" value="10" suffix="pts" title="Duplicate confirm" desc="Already in our database? Still awesome — thanks for strengthening the network."/>
          <RewardCard icon={Zap} tint="emerald" value="25" suffix="pts" title="New discovery" desc="You found one we didn't have. Confetti + instant points."/>
          <RewardCard icon={Trophy} tint="fuchsia" value="+100" suffix="pts" title="Upgrade bonus" desc="When your discovery becomes a paid listing — you keep earning."/>
        </div>
      </section>

      {/* Cash earnings */}
      <section className="bg-gradient-to-r from-amber-900/20 via-black to-amber-900/20 py-16 border-y border-amber-500/20">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] font-black uppercase tracking-widest">
              <Lock size={12}/> Locked Cash Earnings
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-black">Watch your wallet grow.</h2>
            <p className="mt-3 text-neutral-300">
              Every time a business you discovered subscribes to PBB, you earn <b className="text-amber-300">3%</b> of their fee. Every ticket sold on an event you added earns you <b className="text-amber-300">1%</b>. The money locks until <b>April 1, 2027</b> — then unlocks all at once.
            </p>
            {earnings && (
              <div className="mt-5 rounded-xl border border-amber-500/30 bg-black/40 p-4" data-testid="blackout-community-earnings">
                <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-black">Community pot</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-3xl md:text-4xl font-black text-amber-200 tabular-nums" data-testid="community-earnings-total">
                    ${tickTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs text-neutral-400">locked across {earnings.earningMembers || 0} member{earnings.earningMembers === 1 ? '' : 's'}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2">
                    <div className="text-[9px] uppercase tracking-wider text-amber-300/70 font-bold">Subscriptions (3%)</div>
                    <div className="text-amber-200 font-black tabular-nums text-sm">${Number(earnings.subscriptionLockedUsd || 0).toFixed(2)}</div>
                  </div>
                  <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 px-3 py-2">
                    <div className="text-[9px] uppercase tracking-wider text-amber-300/70 font-bold">Tickets (1%)</div>
                    <div className="text-amber-200 font-black tabular-nums text-sm">${Number(earnings.ticketLockedUsd || 0).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <BlackoutCountdown amountUsd={earnings?.totalLockedUsd || 0}/>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between">
          <h2 className="text-3xl md:text-4xl font-black">Top discoverers</h2>
          <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Live</span>
        </div>
        <div className="mt-6 rounded-2xl border border-neutral-800 overflow-hidden" data-testid="blackout-public-leaderboard">
          {board === null ? (
            <div className="p-12 text-center text-neutral-500 text-sm">Loading leaderboard…</div>
          ) : board.length === 0 ? (
            <div className="p-10 text-center">
              <Trophy className="mx-auto text-amber-400 mb-3" size={32}/>
              <div className="font-bold text-lg">The leaderboard is wide open.</div>
              <div className="text-sm text-neutral-400 mt-1">First discovery grabs the #1 spot. Could be you.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-900 text-left text-[10px] uppercase tracking-wider text-neutral-400">
                <tr>
                  <th className="px-4 py-3 w-14">#</th>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3 text-right"><Building2 className="inline" size={12}/> Biz</th>
                  <th className="px-4 py-3 text-right"><Calendar className="inline" size={12}/> Events</th>
                  <th className="px-4 py-3 text-right">Discoveries</th>
                  <th className="px-4 py-3 text-right">Points</th>
                </tr>
              </thead>
              <tbody>
                {board.map(r => (
                  <tr key={r.rank} className="border-t border-neutral-800" data-testid={`blackout-lb-row-${r.rank}`}>
                    <td className="px-4 py-3 font-black text-amber-300">#{r.rank}</td>
                    <td className="px-4 py-3 font-semibold">{r.name}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.businessesDiscovered}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.eventsDiscovered}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.totalDiscoveries}</td>
                    <td className="px-4 py-3 text-right font-black text-emerald-300 tabular-nums">{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-5xl mx-auto px-6 py-20 text-center">
        <h2 className="text-4xl md:text-5xl font-black">Ready to play?</h2>
        <p className="mt-3 text-neutral-400 max-w-xl mx-auto">Create a free account and start earning in under a minute.</p>
        <Link to="/signup" data-testid="blackout-info-register-cta-bottom"
          className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider shadow-xl shadow-emerald-500/30">
          Register to Play <ArrowRight size={18}/>
        </Link>
      </section>
    </div>
  );
}

function RewardCard({ icon: Icon, tint, value, suffix, title, desc }) {
  const colors = {
    emerald: 'from-emerald-500/20 border-emerald-500/40 text-emerald-300',
    amber: 'from-amber-500/20 border-amber-500/40 text-amber-300',
    fuchsia: 'from-fuchsia-500/20 border-fuchsia-500/40 text-fuchsia-300',
  }[tint];
  return (
    <div className={`rounded-2xl border bg-gradient-to-br ${colors} bg-neutral-900 p-6`}>
      <Icon size={24} className="mb-3"/>
      <div className="text-5xl font-black text-white tabular-nums">{value}<span className="text-xl text-neutral-400 ml-1">{suffix}</span></div>
      <div className="mt-2 font-bold">{title}</div>
      <div className="mt-1 text-sm text-neutral-400">{desc}</div>
    </div>
  );
}
